import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useQuery } from '@/api/query'
import { Empty, ErrorState, Loading } from '@/ui/states'
import { STAGE_LABELS, STAGE_TONES, formatScore } from './stages'

type Column = Schema<'BoardColumn'>

/** What each column means, in one line under its heading. The prototype's board
 *  has no such copy and needs it: *Replied* and *Contacted* are the two a new
 *  tenant reads as the same thing. */
const COLUMN_HELP: Record<string, string> = {
  found: 'Found in a post search. Nothing has been sent to them yet.',
  contacted: 'A first touch has gone out. The ladder is running.',
  replied: 'They answered. Their follow-ups have stopped.',
  won: 'Marked won by somebody in your organisation.',
}

/**
 * The CRM board — `design/screens/outreach.html#crm`, audit row §B3.
 *
 * Four columns because §5's pipeline has four states, and the prototype's own
 * note says why it is not the six-stage dashboard funnel: *"posts and opens are
 * things that happen to a lead, not places a lead sits."*
 *
 * WHY THERE IS NO MONEY ON THIS BOARD
 * -------------------------------------
 * The prototype's header reads *"₹8.4L in play"* and its won cards carry
 * *"₹2.4L"*. There is no deal value anywhere in AugRank — §5's pipeline is four
 * states and says nothing about amounts — so every one of those figures would be
 * a number the product invented. The heading carries the count instead, which is
 * true, and `docs/MOCKUP-GAP-AUDIT.md` records the deviation rather than leaving
 * a future session to "fix" it by adding a column nobody asked for.
 *
 * WHY THE CARDS DO NOT DRAG
 * ---------------------------
 * Moving a lead is `POST /outreach/leads/{id}/stage` and it exists; what is not
 * here is drag-and-drop as the way to reach it. A board with four columns and
 * six cards each is a *glance* — the count in the heading is the real figure and
 * the cards are examples — and a drag target on a partial column is an invitation
 * to reorganise a pipeline through a window showing a tenth of it. The move lives
 * on the lead, where the whole of that lead is on screen, and in the inbox, where
 * the conversation that justifies it is.
 */
export function Pipeline(): JSX.Element {
  const board = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/pipeline', { signal })),
    [],
  )

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <h1 className="ar-h1">Pipeline</h1>
        <span className="app-spacer" />
        <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/outreach/leads">
          Open the full list
        </Link>
      </div>

      {board.state.status === 'loading' ? <Loading label="Loading the pipeline" lines={5} /> : null}
      {board.state.status === 'error' ? (
        <ErrorState
          failure={board.state.failure}
          onRetry={board.refetch}
          title="Could not load the pipeline"
        />
      ) : null}
      {board.state.status === 'ready' ? <Board columns={board.state.data.columns} /> : null}
    </div>
  )
}

function Board({ columns }: { columns: Column[] }): JSX.Element {
  const empty = columns.every((column) => column.total === 0)
  if (empty)
    return (
      <div className="ar-card">
        <div className="ar-card__body">
          <Empty
            icon="○"
            title="Nothing in the pipeline yet"
            body="Leads appear here as your browser extension finds them, and move across as they are contacted and reply. The four columns are the whole of the pipeline: found, contacted, replied, won."
          />
        </div>
      </div>
    )

  return (
    <div className="app-board">
      {columns.map((column) => (
        <section className="app-board__col" key={column.stage}>
          <header className="app-board__head">
            <span>{STAGE_LABELS[column.stage] ?? column.stage}</span>
            <span className={`ar-badge ar-badge--${STAGE_TONES[column.stage] ?? 'neutral'}`}>
              {column.total}
            </span>
          </header>
          <p className="ar-subtle app-board__help">{COLUMN_HELP[column.stage] ?? ''}</p>
          {column.cards.length === 0 ? (
            <p className="ar-subtle app-board__empty">Nothing here.</p>
          ) : (
            column.cards.map((lead) => (
              <Link className="app-board__card" key={lead.id} to={`/outreach/leads/${lead.id}`}>
                <strong>{lead.full_name ?? 'Unnamed lead'}</strong>
                <span className="ar-subtle">
                  {[lead.company, `score ${formatScore(lead.relevance_score ?? null)}`]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </Link>
            ))
          )}
          {column.total > column.cards.length ? (
            <Link className="ar-link app-board__more" to={`/outreach/leads?stage=${column.stage}`}>
              {column.total - column.cards.length} more
            </Link>
          ) : null}
        </section>
      ))}
    </div>
  )
}
